<!--
  MonitoringView.vue — the live monitoring page (route '/', component `online`).
  Purpose: assemble the realtime engine. On create it registers the engine's `states`
  (StateManager) + `trackers` (roster) modules via registerRealtime(store) — trackers
  first so the source_id -> tracker join getters resolve — fetches the roster
  (trackers/load = tracker/list), then starts the transport-agnostic states channel
  (states/load), which prefers the atmosphere.js websocket (event/subscription) and
  falls back to the tracker/get_states poller. The list + telemetry read from the
  engine getters trackers/trackerList and states/stateByTrackerId. The Leaflet map is
  a Tier-2 engine (map framework), shown here as a slot.
  Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
  (registerRealtime; actions trackers/load + states/load; getters
  trackers/trackerList + states/stateByTrackerId; state fields gps.location /
  movement_status / connection_status — realtime-pipeline.md), internal impl re-authored.
-->
<template>
  <!-- Full-bleed map with the floating "Objects" panel, matching /#/online/trackers. -->
  <div class="online-screen">
    <component
      :is="mapEngine"
      ref="map"
      :trackers="trackers"
      :states="statesById"
      :active-id="activeId"
      :map-layers="mapLayers"
      :places="places"
      :geofences="geofences"
      :groups="groups"
      :display="display"
      :icon-overrides="iconOverrides"
      @marker-click="onShowWidgets($event)"
      @reference-set="onReferenceSet"
    ></component>

    <transition name="op-slide">
    <objects-panel
      v-if="!engineError && !panelCollapsed"
      :trackers="trackers"
      :states="statesById"
      :active-id="activeId"
      :groups="groups"
      :chat-unread="chatUnread"
      :ref-point="refPoint"
      :history-active="historyOn"
      @focus="onFocus"
      @history="historyFab"
      @show-widgets="onShowWidgets"
      @chat="chatTrackerId = $event.id"
      @collapse="panelCollapsed = true"
      @display-change="onDisplay"
      @fit-all="fitAll"
      @row-action="onRowAction"
      @pick-reference="onPickReference"
      @need-reference="onNeedReference"
      @selection-change="onSelectionChange"
    ></objects-panel>
    </transition>

    <widgets-panel
      v-if="widgetsTracker && !historyOn"
      :tracker="widgetsTracker"
      :state="widgetsState"
      :trackers="trackers"
      :states-by-id="statesById"
      :objects-visible="!panelCollapsed"
      @close="widgetsTrackerId = null"
      @select-tracker="onWidgetSelect"
    ></widgets-panel>

    <chat-panel
      v-if="chatTracker"
      :tracker="chatTracker"
      @close="onChatClose"
    ></chat-panel>

    <icon-picker-dialog
      v-if="iconDialogTracker"
      :tracker="iconDialogTracker"
      :override="iconOverrides[iconDialogTrackerId] || null"
      @save="onIconSave"
      @close="iconDialogTrackerId = null"
    ></icon-picker-dialog>

    <geo-link-dialog
      v-if="geoLinkTracker"
      :tracker="geoLinkTracker"
      @close="geoLinkTrackerId = null"
    ></geo-link-dialog>

    <button v-if="!engineError && panelCollapsed" class="op-reopen" title="Show objects" @click="panelCollapsed = false">
      <i class="material-icons">menu</i>
    </button>

    <history-period-dialog
      v-if="historyDialogOpen"
      :seed-from="historyFrom"
      :seed-to="historyTo"
      @select="onHistorySelect"
      @close="historyDialogOpen = false"
    ></history-period-dialog>

    <travel-history-panel
      v-if="historyOn && historyPoints.length"
      :points="historyPoints"
      :events="historyEvents"
      :range-label="historyRangeLabel"
      :objects-visible="!panelCollapsed"
      :color-mode="historyColorMode"
      :settings="historySettings"
      @seek="onHistorySeek"
      @change-period="historyDialogOpen = true"
      @close="closeHistory"
      @collapse="panelCollapsed = true"
      @color-mode="onHistoryColorMode"
      @setting="onHistorySetting"
      @select-trip="onHistorySelectTrip"
      @focus-event="onHistoryFocusEvent"
      @event-markers="onHistoryEventMarkers"
      @export-kml="onHistoryExportKml"
      @print="onHistoryPrint"
    ></travel-history-panel>

    <div v-if="toast" class="online-toast">{{ toast }}</div>

    <div v-if="engineError" class="online-engine-error">
      Realtime engine (src/realtime) is not wired — see BUILD.md.
    </div>
  </div>
</template>

<script>
import { registerModuleSafe } from '@/store/register-module-safe'
// Leaflet-engine map — the real go.lacak.io stack (Leaflet + GoogleMutant tiles + PruneCluster
// + hotline + ant-path) and now the DEFAULT engine. Statically imported so the default map path
// renders without waiting on an extra async chunk. (Its heavy runtime plugins still load lazily
// inside the component's mounted() via loadLeafletStack, after window.google.maps is ready.)
import TrackerMapLeaflet from '@/map/TrackerMapLeaflet.vue'
// Classic Google-Maps JS API engine, kept as the ?engine=google (=classic) fallback. Lazy so its
// bundle (Google markerclusterer etc.) isn't pulled into the default Leaflet path.
const TrackerMap = () => import('@/map/TrackerMap.vue')
import ObjectsPanel from '@/shell/ObjectsPanel.vue'
import WidgetsPanel from '@/shell/WidgetsPanel.vue'
import TravelHistoryPanel from '@/shell/TravelHistoryPanel.vue'
import HistoryPeriodDialog from '@/shell/HistoryPeriodDialog.vue'
import { splitTrips } from '@/shell/track-trips.js'
import ChatPanel from '@/shell/ChatPanel.vue'
import IconPickerDialog from '@/shell/IconPickerDialog.vue'
import GeoLinkDialog from '@/shell/GeoLinkDialog.vue'

export default {
  name: 'MonitoringView',
  components: { TrackerMap, 'tracker-map-leaflet': TrackerMapLeaflet, ObjectsPanel, WidgetsPanel, TravelHistoryPanel, HistoryPeriodDialog, ChatPanel, IconPickerDialog, GeoLinkDialog },
  data() {
    return {
      engineError: false,
      // tracker currently focused from the Objects panel / a marker click
      activeId: null,
      // custom map-layer overlays (map_layer/list) for the map's Layers panel
      mapLayers: [],
      // saved places / POIs (place/list) rendered on the map
      places: [],
      // geofences/zones (zone/list) for the map's Geofences panel
      geofences: [],
      // tracker groups (tracker/group/list) for the Objects panel grouping
      groups: [],
      // Objects panel collapse + display settings (from the panel's settings gear)
      panelCollapsed: false,
      display: { labels: true, tails: true, cluster: true, animation: true, follow: true, showOnlySelected: false, hideOffline: false, showInfoByClick: false, selectedIds: {} },
      // reference point {lat,lng} for the Objects panel "sort by distance"
      refPoint: null,
      historyOn: false,
      historyPoints: [],
      historyEvents: [],
      historyDialogOpen: false,
      historyFrom: null,
      historyTo: null,
      // travel-history panel state (owned here so the map + panel never disagree)
      historyColorMode: 'tracks',
      historySettings: { showStops: true, showLBS: false, clustered: false, smartFilter: false, splitByStops: true },
      historyIndex: 0, // current playback point index (kept so a recolour redraw can restore it)
      toast: '',
      // tracker whose details/widgets panel is open (info icon / marker click)
      widgetsTrackerId: null,
      // tracker whose chat window is open (row ⋮ → Chat)
      chatTrackerId: null,
      // change-icon dialog + per-tracker icon/color overrides
      iconDialogTrackerId: null,
      iconOverrides: {},
      // "Share location" geo-link dialog target tracker
      geoLinkTrackerId: null,
      // per-tracker unread chat counts (row chat badge)
      chatUnread: {},
      // Reactive epoch bumped once the lazily-registered realtime modules exist.
      // The roster/telemetry Vuex modules are registered inside async created(), so
      // these computeds first evaluate BEFORE the getter keys exist. A Vue computed
      // caches and — critically — captures no dependency on a getter it never read,
      // so it would stay empty forever ($forceUpdate does NOT invalidate a computed
      // cache). Referencing `moduleEpoch` gives them a dependency we can bump after
      // registration; that first real read then also captures the live getter dep,
      // so subsequent socket/poller commits keep them reactive on their own.
      moduleEpoch: 0
    }
  },
  computed: {
    // Which map engine to mount. Default → the real Leaflet stack (matches go.lacak.io);
    // ?engine=google (or =classic) forces the classic Google-Maps JS API engine as an escape
    // hatch. Both share the same props/emits/methods contract, so the swap is transparent.
    mapEngine() {
      const e = this.$route.query.engine
      return e === 'google' || e === 'classic' ? 'TrackerMap' : 'tracker-map-leaflet'
    },
    trackers() {
      this.moduleEpoch // eslint-disable-line no-unused-expressions
      const g = this.$store.getters
      return g['trackers/trackerList'] || g['trackers/list'] || []
    },
    statesById() {
      this.moduleEpoch // eslint-disable-line no-unused-expressions
      return this.$store.getters['states/stateByTrackerId'] || {}
    },
    widgetsTracker() {
      return this.widgetsTrackerId != null ? this.trackers.find((t) => t.id === this.widgetsTrackerId) || null : null
    },
    widgetsState() {
      return this.widgetsTrackerId != null ? (this.statesById[this.widgetsTrackerId] || {}) : {}
    },
    chatTracker() {
      return this.chatTrackerId != null ? this.trackers.find((t) => t.id === this.chatTrackerId) || null : null
    },
    iconDialogTracker() {
      return this.iconDialogTrackerId != null ? this.trackers.find((t) => t.id === this.iconDialogTrackerId) || null : null
    },
    geoLinkTracker() {
      return this.geoLinkTrackerId != null ? this.trackers.find((t) => t.id === this.geoLinkTrackerId) || null : null
    },
    historyRangeLabel() {
      if (!this.historyFrom) return ''
      // Full "Month D, YYYY h:mm AM - Month D, YYYY h:mm PM" like the real go.lacak.io date chip.
      const f = new Date(this.historyFrom); const t = new Date(this.historyTo)
      const dOpt = { year: 'numeric', month: 'long', day: 'numeric' }
      const tOpt = { hour: 'numeric', minute: '2-digit', hour12: true }
      const fmt = (d) => d.toLocaleDateString(undefined, dOpt) + ' ' + d.toLocaleTimeString(undefined, tOpt)
      return fmt(f) + ' - ' + fmt(t)
    }
  },
  watch: {
    // When the Objects panel collapses/expands the visible map area changes, so
    // re-centre the selected marker into the now-visible area (source behaviour).
    panelCollapsed() {
      this.$nextTick(() => setTimeout(() => { if (this.$refs.map && this.$refs.map.recenterActive) this.$refs.map.recenterActive() }, 180))
    },
    // Navigating to a /trips/tracks/... deep-link while the app is already mounted
    // (same component instance — created() won't re-run) must still open history.
    $route() { this.maybeOpenHistoryFromRoute() }
  },
  async created() {
    // Step one: register the realtime engine's modules. A failure HERE means the
    // engine itself is missing/mis-shaped -> show the "not wired" note.
    try {
      const rt = await import('@/realtime')
      const register = rt.registerRealtime || rt.default
      if (typeof register === 'function') {
        register(this.$store)
      } else {
        const trackersModule = rt.trackersModule
        const statesModule = rt.statesModule
        if (trackersModule) registerModuleSafe(this.$store, 'trackers', trackersModule)
        if (statesModule) registerModuleSafe(this.$store, 'states', statesModule)
        if (!trackersModule && !statesModule) {
          throw new Error('realtime barrel exports no registerRealtime/statesModule')
        }
      }
    } catch (err) {
      this.engineError = true
      // eslint-disable-next-line no-console
      console.warn('[tracko] monitoring: realtime engine not available:', err && err.message)
      return
    }

    // Step two: fetch the roster + start the channel. A failure HERE is just a
    // data/session problem, e.g. no backend in dev -> keep the empty list rather
    // than blaming the engine.
    try {
      await this.$store.dispatch('trackers/load')
      // Guard the async lifecycle race: if the view was destroyed while the engine
      // import / roster fetch was in flight, beforeDestroy already ran its
      // states/clear (a no-op then, because the channel didn't exist yet). Starting
      // the channel now would orphan a poller that nothing ever stops. Bail instead.
      if (this._isBeingDestroyed || this._isDestroyed) return
      // Reveal the roster NOW (invalidate the lazy computeds) so it renders even if the
      // telemetry channel below fails to start — a fetched roster must never stay hidden.
      this.moduleEpoch++
      await this.$store.dispatch('states/load')
      if (this._isBeingDestroyed || this._isDestroyed) {
        this.$store.commit('states/clear')
        return
      }
      // Fetch the custom map-layer overlays + tracker groups + chat unread counts.
      import('@/api/request').then(({ request }) => {
        request('map_layer/list', {}, 'list').then((list) => { this.mapLayers = list || [] }).catch(() => {})
        request('place/list', {}, 'list').then((list) => { this.places = list || [] }).catch(() => {})
        request('zone/list', {}, 'list').then((list) => { this.geofences = list || [] }).catch(() => {})
        request('tracker/group/list', {}, 'list').then((list) => { this.groups = list || [] }).catch(() => {})
        this.loadChatUnread()
      }).catch(() => {})
      // Modules now exist and the store has data — invalidate the roster/telemetry
      // computeds so they re-read the (now-present) getters and capture their live
      // dependency. See the `moduleEpoch` note in data().
      this.moduleEpoch++
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[tracko] monitoring: roster/states load failed (backend/session?):', err && err.message)
    }
  },
  mounted() {
    // Cold load on a /trips/tracks/... deep-link: the map component is mounted here but
    // its engine is still loading async, so maybeOpenHistoryFromRoute waits for map-ready.
    // Runs independent of created()'s async roster fetch (history only needs track/read).
    this.maybeOpenHistoryFromRoute()
  },
  beforeDestroy() {
    clearTimeout(this._toastT)
    clearTimeout(this._mapReadyT)
    // Symmetric teardown — the states `clear` mutation stops the channel.
    try {
      this.$store.commit('states/clear')
    } catch (e) {
      /* module may not be registered */
    }
  },
  methods: {
    // Path A — a soft list-row click: select + pan the object into view, NO forced
    // zoom and NO follow (04-interaction-live.md §1a). Re-assign even if the id is
    // unchanged so re-clicking a row re-centres it.
    onFocus(t) {
      this.activeId = null
      this.$nextTick(() => {
        this.activeId = t.id
        // Keep an open details/widgets panel in sync with the newly-selected object.
        if (this.widgetsTrackerId != null) this.widgetsTrackerId = t.id
        if (this.historyOn) this.loadHistory()
        this.$nextTick(() => { if (this.$refs.map && this.$refs.map.softSelect) this.$refs.map.softSelect(t.id) })
      })
    },
    // Path B — marker/cluster click, info activator, or widget dropdown: open the
    // details/widgets panel and zoom-15 + FOLLOW the object (§1b showWidgetsAndFocus).
    onShowWidgets(t) {
      this.widgetsTrackerId = t.id
      this.activeId = null
      this.$nextTick(() => {
        this.activeId = t.id
        if (this.historyOn) this.loadHistory()
        this.$nextTick(() => { if (this.$refs.map && this.$refs.map.focus) this.$refs.map.focus(t.id) })
      })
    },
    // Widgets header dropdown picked another tracker → Path B (switch panel + focus/follow).
    onWidgetSelect(id) {
      const t = this.trackers.find((x) => x.id === id)
      if (t) this.onShowWidgets(t)
    },
    loadChatUnread() {
      import('@/api/request').then(({ request }) => {
        request('tracker/chat/unread/list', {}, 'value').then((v) => { this.chatUnread = v || {} }).catch(() => {})
      }).catch(() => {})
    },
    onChatClose() {
      this.chatTrackerId = null
      this.loadChatUnread() // opening a chat marks it read server-side; refresh badges
    },
    onDisplay(d) {
      // preserve the current selection set across display-toggle updates
      this.display = Object.assign({}, d, { selectedIds: this.display.selectedIds })
      if (this.$refs.map && this.$refs.map.applyDisplay) this.$refs.map.applyDisplay(this.display)
    },
    // Objects panel "Set reference point" (gps_fixed) → put the map into pick mode.
    onPickReference() { if (this.$refs.map && this.$refs.map.startPickReference) this.$refs.map.startPickReference() },
    // "Sort by distance" with no reference point yet → the real toast + auto-enter pick mode.
    onNeedReference() {
      this.flash("Sorting 'by distance' requires to set the Reference Point")
      this.onPickReference()
    },
    // Map click placed the reference marker → drive the panel's distance sort.
    onReferenceSet(ll) { this.refPoint = ll },
    // Panel checkbox selection changed → let the map honour "Show only selected objects".
    onSelectionChange(ids) {
      this.display = Object.assign({}, this.display, { selectedIds: ids })
      if (this.$refs.map && this.$refs.map.applyDisplay) this.$refs.map.applyDisplay(this.display)
    },
    fitAll() { if (this.$refs.map && this.$refs.map.fitAll) this.$refs.map.fitAll() },
    // FAB: when history is on, turn it off; otherwise open the "Select the period" dialog.
    historyFab() {
      if (this.historyOn) { this.closeHistory(); return }
      if (this.activeId == null) { this.flash('Select an object first to view its track history'); return }
      this.historyDialogOpen = true
    },
    closeHistory() {
      // Invalidate any in-flight loadHistory() so a late response can't resurrect the track.
      this._histReqSeq = (this._histReqSeq || 0) + 1
      this.historyOn = false
      this.historyPoints = []
      this.historyEvents = []
      if (this.$refs.map) this.$refs.map.clearHistoryTrack()
      // Drop the deep-link out of the URL so a later re-open isn't fighting the route,
      // and so the map screen shows a clean path. No-op if we're not on the deep-link.
      if (this.$route.name === 'trips-tracks') { this._histLinkSig = null; this.$router.replace('/').catch(() => {}) }
    },
    // Open history straight from a /trips/tracks/:id/:from/:to deep-link. Reuses the exact
    // same backend call as the period dialog (loadHistory → track/read, the go.lacak.io API
    // format), so a shared URL reconstructs the same track. `from`/`to` are ISO strings
    // already decoded by vue-router; `id` is the tracker id.
    maybeOpenHistoryFromRoute() {
      const r = this.$route
      if (!r || r.name !== 'trips-tracks') return
      const { id, from, to } = r.params
      if (id == null || !from || !to) return
      const sig = id + '|' + from + '|' + to
      if (this._histLinkSig === sig) return // already applied this exact link
      if (isNaN(new Date(from).getTime()) || isNaN(new Date(to).getTime())) { this.flash('This history link is invalid'); return }
      this._histLinkSig = sig
      this.activeId = /^\d+$/.test(String(id)) ? parseInt(id, 10) : id
      this.historyFrom = from
      this.historyTo = to
      this.historyOn = true
      this.historyDialogOpen = false
      // The map engine loads async; draw only once its map instance exists.
      this.whenMapReady(() => this.loadHistory())
    },
    // Poll (bounded) until the mounted map engine's instance is ready, then run cb. Both
    // engines expose a truthy `map` when initialised; gives up silently after ~6s.
    whenMapReady(cb, tries) {
      if (this._isDestroyed || this._isBeingDestroyed) return
      const m = this.$refs.map
      if (m && m.map) { cb(); return }
      if ((tries || 0) >= 120) return
      this._mapReadyT = setTimeout(() => this.whenMapReady(cb, (tries || 0) + 1), 50)
    },
    onHistorySelect({ from, to }) {
      this.historyDialogOpen = false
      this.historyFrom = from
      this.historyTo = to
      this.historyOn = true
      this.loadHistory()
    },
    async loadHistory() {
      if (this.activeId == null || !this.historyFrom) return
      // Monotonic request token: a newer loadHistory() — or closeHistory() — invalidates older
      // in-flight ones. Guards the WHOLE flow (not just events) so an out-of-order track/read
      // can't overwrite the current track, re-fit the map, wipe fresh events, or resurrect a
      // track after the panel was closed. current() = this is still the active load AND history
      // is still open.
      const seq = (this._histReqSeq = (this._histReqSeq || 0) + 1)
      const current = () => seq === this._histReqSeq && this.historyOn
      try {
        const { request } = await import('@/api/request')
        const hours = Math.max(1, Math.round((new Date(this.historyTo) - new Date(this.historyFrom)) / 3600000))
        const points = await request('track/read', { tracker_id: this.activeId, from: this.historyFrom, to: this.historyTo, period_hours: hours }, 'list')
        if (!current()) return // superseded by a newer load, or history was closed mid-flight
        this.historyPoints = points || []
        // No data for the chosen window → surface it (like the real app) instead of an
        // empty/dead playback bar.
        if (!this.historyPoints.length) { this.flash('No track data for the selected period'); this.closeHistory(); return }
        this.historyIndex = 0
        this.drawHistory(true)
        // History events feed for the EVENTS tab (real action string; separate call so a missing/
        // empty feed never blocks the track from drawing). The same request token guards the
        // assignment so an out-of-order / cross-tracker response can't overwrite a newer load.
        this.historyEvents = []
        request('history/tracker/list', { tracker_id: this.activeId, from: this.historyFrom, to: this.historyTo, period_hours: hours, ascending: true, limit: 200 }, 'list')
          .then((list) => { if (current()) this.historyEvents = list || [] })
          .catch(() => { if (current()) this.historyEvents = [] })
      } catch (e) {
        // A newer load already took over → stay silent. Otherwise surface the failure (an
        // expired/invalid deep-link or backend error) instead of a silent teardown, then tear
        // down so a previously-drawn track can't linger with no bar/FAB to dismiss it.
        if (!current()) return
        this.flash('Could not load track history')
        this.closeHistory()
      }
    },
    // (Re)draw the track for the current colour mode + settings. Trips are computed once here
    // (shared util) so the map colouring and the panel's trip list agree on boundaries. `fit`
    // only on the first draw; a recolour/settings redraw keeps the current view AND re-applies
    // the playback marker at its current index (else it would snap back to the track start).
    drawHistory(fit) {
      if (!this.$refs.map || !this.historyPoints.length) return
      const { trips, stops } = splitTrips(this.historyPoints, { splitByStops: this.historySettings.splitByStops !== false })
      this.$refs.map.showHistoryTrack(this.historyPoints, {
        colorMode: this.historyColorMode,
        trips,
        stops,
        showStops: !!this.historySettings.showStops,
        fit: fit === true,
      })
      if (!fit && this.historyIndex > 0 && this.$refs.map.setHistoryPosition) this.$refs.map.setHistoryPosition(this.historyIndex)
    },
    onHistorySeek(i) { this.historyIndex = i; if (this.$refs.map) this.$refs.map.setHistoryPosition(i) },
    onHistoryColorMode(mode) { this.historyColorMode = mode; this.drawHistory(false) },
    onHistorySetting(key) {
      this.$set(this.historySettings, key, !this.historySettings[key])
      // Only the map-affecting toggles need a redraw; the panel reacts to the prop itself.
      if (key === 'splitByStops' || key === 'showStops') this.drawHistory(false)
    },
    onHistorySelectTrip(trip) { if (this.$refs.map && this.$refs.map.fitTrackRange) this.$refs.map.fitTrackRange(trip.startIdx, trip.endIdx) },
    // Clicking an event in the EVENTS tab: the panel already seeks its own playback; here we
    // move the map's playback marker to the matching index so the map + panel stay in sync.
    onHistoryFocusEvent(i) { if (i != null && i >= 0) this.onHistorySeek(i) },
    // The EVENTS tab publishes the set of visible event markers → draw them on the map's events-layer.
    onHistoryEventMarkers(list) { if (this.$refs.map && this.$refs.map.showEventMarkers) this.$refs.map.showEventMarkers(list) },
    // Serialise the loaded track as a KML document — a real, client-side export (no backend
    // needed; the points are already in the browser). A LineString for the path plus Start/End
    // placemarks, matching what a Google-Earth/GIS import expects (coords are lng,lat,alt).
    buildHistoryKml() {
      const pts = this.historyPoints || []
      if (pts.length < 1) return ''
      const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
      const t = this.trackers.find((x) => x.id === this.activeId)
      const name = (t && t.label) || ('Tracker ' + this.activeId)
      const coords = pts.map((p) => p.lng + ',' + p.lat + ',0').join(' ')
      const a = pts[0]; const b = pts[pts.length - 1]
      const placemark = (label, p, styleUrl) =>
        '  <Placemark>\n    <name>' + esc(label) + '</name>\n    <styleUrl>' + styleUrl + '</styleUrl>\n' +
        '    <Point><coordinates>' + p.lng + ',' + p.lat + ',0</coordinates></Point>\n  </Placemark>\n'
      return '<?xml version="1.0" encoding="UTF-8"?>\n' +
        '<kml xmlns="http://www.opengis.net/kml/2.2">\n<Document>\n' +
        '  <name>' + esc(name) + ' — track</name>\n' +
        '  <description>' + esc(this.historyRangeLabel) + '</description>\n' +
        '  <Style id="trackLine"><LineStyle><color>ff2e88c7</color><width>4</width></LineStyle></Style>\n' +
        '  <Style id="startPt"><IconStyle><color>ff4baf2e</color></IconStyle></Style>\n' +
        '  <Style id="endPt"><IconStyle><color>ff3539e5</color></IconStyle></Style>\n' +
        '  <Placemark>\n    <name>' + esc(name) + '</name>\n    <styleUrl>#trackLine</styleUrl>\n' +
        '    <LineString>\n      <tessellate>1</tessellate>\n      <coordinates>' + coords + '</coordinates>\n    </LineString>\n  </Placemark>\n' +
        placemark('Start', a, '#startPt') + placemark('End', b, '#endPt') +
        '</Document>\n</kml>\n'
    },
    onHistoryExportKml() {
      const kml = this.buildHistoryKml()
      if (!kml) { this.flash('No track to export'); return }
      try {
        const stamp = (this.historyFrom || '').slice(0, 10) || 'track'
        const fname = 'track-' + this.activeId + '-' + stamp + '.kml'
        const blob = new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = fname
        document.body.appendChild(a); a.click()
        // Revoke on the next tick so the download has grabbed the blob first.
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url) }, 0)
        this.flash('Exported ' + fname)
      } catch (e) {
        this.flash('KML export failed')
      }
    },
    // Build a print-friendly track report (summary + trips + events) from the loaded data —
    // a self-contained HTML document, so it prints cleanly instead of the whole app chrome.
    buildHistoryPrintHtml() {
      const pts = this.historyPoints || []
      if (!pts.length) return ''
      const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
      const t = this.trackers.find((x) => x.id === this.activeId)
      const name = (t && t.label) || ('Tracker ' + this.activeId)
      const { trips, stops } = splitTrips(pts, { splitByStops: this.historySettings.splitByStops !== false })
      const fmtTime = (v) => { try { return new Date(v).toLocaleString() } catch (e) { return String(v) } }
      const fmtDur = (ms) => { const m = Math.round((ms || 0) / 60000); return m < 60 ? m + ' min' : Math.floor(m / 60) + 'h ' + (m % 60) + 'm' }
      const totalKm = trips.reduce((s, tr) => s + (tr.distanceKm || 0), 0)
      const totalMs = trips.reduce((s, tr) => s + (tr.durationMs || 0), 0)
      const maxSpeed = pts.reduce((m, p) => Math.max(m, p.speed || 0), 0)
      const tripRows = trips.map((tr, i) =>
        '<tr><td>' + (i + 1) + '</td><td>' + esc(fmtTime(tr.from)) + '</td><td>' + esc(fmtTime(tr.to)) +
        '</td><td>' + (tr.distanceKm || 0).toFixed(1) + ' km</td><td>' + esc(fmtDur(tr.durationMs)) + '</td><td>' + (tr.maxSpeed || 0) + ' km/h</td></tr>').join('')
      const evRows = (this.historyEvents || []).map((e) =>
        '<tr><td>' + esc(fmtTime(e.time)) + '</td><td>' + esc(e.type || '') + '</td><td>' + esc(e.message || '') + '</td></tr>').join('')
      const eventsSection = (this.historyEvents && this.historyEvents.length)
        ? '<h2>Events</h2><table><thead><tr><th>Time</th><th>Type</th><th>Detail</th></tr></thead><tbody>' + evRows + '</tbody></table>'
        : ''
      return '<!doctype html><html><head><meta charset="utf-8"><title>' + esc(name) + ' — track report</title><style>' +
        'body{font:13px/1.5 Arial,Helvetica,sans-serif;color:#263238;margin:28px;}' +
        'h1{font-size:20px;margin:0 0 2px;}h2{font-size:15px;margin:22px 0 8px;border-bottom:2px solid #1e88c7;padding-bottom:4px;}' +
        '.meta{color:#607d8b;margin-bottom:16px;}' +
        '.cards{display:flex;flex-wrap:wrap;gap:10px;margin:12px 0;}' +
        '.card{border:1px solid #cfd8dc;border-radius:6px;padding:8px 14px;min-width:96px;}' +
        '.card .k{font-size:11px;color:#90a4ae;text-transform:uppercase;letter-spacing:.04em;}' +
        '.card .v{font-size:17px;font-weight:600;}' +
        'table{border-collapse:collapse;width:100%;margin-top:4px;}' +
        'th,td{text-align:left;padding:6px 8px;border-bottom:1px solid #eceff1;font-size:12px;}' +
        'th{background:#f2f6f9;color:#546e7a;}' +
        '@media print{body{margin:0;}}' +
        '</style></head><body>' +
        '<h1>' + esc(name) + ' — track report</h1>' +
        '<div class="meta">' + esc(this.historyRangeLabel) + ' · ' + pts.length + ' points</div>' +
        '<div class="cards">' +
        '<div class="card"><div class="k">Distance</div><div class="v">' + totalKm.toFixed(1) + ' km</div></div>' +
        '<div class="card"><div class="k">Driving</div><div class="v">' + esc(fmtDur(totalMs)) + '</div></div>' +
        '<div class="card"><div class="k">Trips</div><div class="v">' + trips.length + '</div></div>' +
        '<div class="card"><div class="k">Stops</div><div class="v">' + stops.length + '</div></div>' +
        '<div class="card"><div class="k">Max speed</div><div class="v">' + Math.round(maxSpeed) + ' km/h</div></div>' +
        '</div>' +
        '<h2>Trips</h2><table><thead><tr><th>#</th><th>Start</th><th>End</th><th>Distance</th><th>Duration</th><th>Max speed</th></tr></thead><tbody>' +
        (tripRows || '<tr><td colspan="6">No trips in this period.</td></tr>') + '</tbody></table>' +
        eventsSection +
        '</body></html>'
    },
    onHistoryPrint() {
      const html = this.buildHistoryPrintHtml()
      if (!html) { this.flash('No track to print'); return }
      // Print via a hidden iframe (no popup blocker, prints only the report, not the app chrome).
      try {
        const iframe = document.createElement('iframe')
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
        document.body.appendChild(iframe)
        // onload and the readyState fallback can both fire — run the print exactly once.
        let done = false
        const run = () => {
          if (done) return
          done = true
          try { iframe.contentWindow.focus(); iframe.contentWindow.print() } catch (e) { /* print unavailable */ }
          setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe) }, 800)
        }
        iframe.onload = () => setTimeout(run, 60)
        // html is built entirely in-code with every dynamic value passed through esc(), so the
        // document.write sink carries no unescaped input.
        const doc = iframe.contentWindow.document
        doc.open(); doc.write(html); doc.close()
        // Some browsers don't fire onload for document.write — fall back on readyState.
        if (doc.readyState === 'complete') setTimeout(run, 60)
      } catch (e) {
        this.flash('Print failed')
      }
    },
    onRowAction({ action, tracker }) {
      const label = tracker && tracker.label ? tracker.label : ''
      switch (action) {
        case 'chat': this.chatTrackerId = tracker.id; return
        case 'change-icon': this.iconDialogTrackerId = tracker.id; return
        case 'device-settings': this.$router.push('/devices?tracker=' + tracker.id); return
        // Open the report builder pre-selected for this object (a tracker id is NOT a
        // report id — the report must be generated first).
        case 'reports': this.$router.push('/reports?tracker=' + tracker.id); return
        case 'alert-rules': this.flash('Open alert rules · ' + label); return
        case 'share': this.geoLinkTrackerId = tracker.id; return
        default: this.flash(action + ' · ' + label)
      }
    },
    onIconSave(payload) {
      this.$set(this.iconOverrides, payload.trackerId, { icon: payload.icon, color: payload.color })
      if (this.$refs.map && this.$refs.map.applyIconOverride) this.$refs.map.applyIconOverride(payload.trackerId)
    },
    flash(msg) {
      this.toast = msg
      clearTimeout(this._toastT)
      this._toastT = setTimeout(() => { this.toast = '' }, 2200)
    }
  }
}
</script>

<style scoped>
.online-screen {
  position: relative;
  height: 100%;
  width: 100%;
  /* Panel width — the real go.lacak.io object/widget panels are ~340px, not half the
     screen; cap it there so wide monitors don't get 460px slabs that crowd the map.
     Objects + Widgets + History panels and the history FAB all size/position from this. */
  --panel-w: clamp(300px, 22vw, 348px);
  --panel-gap: 20px;
}
.online-engine-error {
  position: absolute;
  top: 16px;
  left: 16px;
  background: #fff;
  padding: 12px 16px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  color: #607d8b;
  z-index: 600;
}
.op-reopen {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 620;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
  color: #37474f;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.online-toast {
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 700;
  background: rgba(38, 50, 56, 0.94);
  color: #fff;
  padding: 9px 18px;
  border-radius: 20px;
  font: 13px Roboto, sans-serif;
}
/* Smooth slide+fade when the Objects list minimizes/expands. */
.op-slide-enter-active,
.op-slide-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.op-slide-enter,
.op-slide-leave-to {
  transform: translateX(-24px);
  opacity: 0;
}
</style>
